import { useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { Alert, Loader, Stack } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type { UserAttributeDto } from '@hotjar/feedback/domain/common/UserAttributes/UserAttributeDto';
import { TrafficCoverage } from '@hotjar/feedback/components/common/Wizard/TrafficCoverage';
import { MultipleWidgetHelp } from '@hotjar/feedback/components/common/Wizard/MultipleWidgetHelp';
import { PagesTargeting } from './PagesTargeting';
import { DeviceSelector } from '../../../common/Wizard/DeviceSelector';
import type { TargetEntry, DeviceTargetEntry } from '../../../common/Wizard/common/targeting';

import type { Device } from '../../../../domain/common/Responses/Device';
import { UserAttributesTargeting } from './UserAttributesTargeting/UserAttributesTargeting';

const deviceEntries: Record<Device, DeviceTargetEntry> = {
  desktop: {
    id: uuid(),
    target: { component: 'device', pattern: 'desktop', match_operation: 'exact' },
    error: undefined,
  },
  phone: {
    id: uuid(),
    target: { component: 'device', pattern: 'phone', match_operation: 'exact' },
    error: undefined,
  },
  tablet: {
    id: uuid(),
    target: { component: 'device', pattern: 'tablet', match_operation: 'exact' },
    error: undefined,
  },
} as const;

export type TargetingSectionProps = {
  hasTargetingErrors: boolean;
  targeting: undefined | TargetEntry[];
  isOffsite: boolean;
  targetingAutoValidate: boolean;
  userAttributes: UserAttributeDto[] | undefined;
  targetingPercentage: number;
  onTargetingPercentageChange: (percentage: number | undefined) => void;
  onTargetingChange: (targeting: TargetEntry[]) => void;
  onUpsell: () => void;
};

export const TargetingSection = ({
  targeting,
  hasTargetingErrors,
  isOffsite,
  targetingAutoValidate,
  userAttributes,
  targetingPercentage,
  onTargetingPercentageChange,
  onTargetingChange,
  onUpsell,
}: TargetingSectionProps): JSX.Element | null => {
  const { t } = useTranslation('feedback', { keyPrefix: 'survey-wizard.targeting' });

  const targetedDevices = useMemo(
    () =>
      targeting
        ?.filter((rule): rule is DeviceTargetEntry => rule.target.component === 'device')
        .map((rule) => rule.target.pattern),
    [targeting],
  );

  const onDevicesChanged = useCallback(
    (devices: Device[]) => {
      if (targeting === undefined) return;
      const newEntries = [
        ...targeting.filter((entry) => entry.target.component !== 'device'),
        ...devices.map((device) => deviceEntries[device]),
      ];
      onTargetingChange(newEntries);
    },
    [onTargetingChange, targeting],
  );

  if (targeting === undefined) return null;

  return (
    <Stack direction="column" spacing="x3">
      {hasTargetingErrors && <Alert>{t('review-errors')}</Alert>}

      {targetedDevices !== undefined && (
        <DeviceSelector
          devices={targetedDevices}
          onDevicesChanged={onDevicesChanged}
          context={isOffsite ? 'invitation' : 'survey'}
        />
      )}

      <PagesTargeting
        onUpsell={onUpsell}
        onTargetingChange={onTargetingChange}
        targeting={targeting}
        autoValidate={targetingAutoValidate}
        entityType={isOffsite ? 'invitation' : 'survey'}
        hasErrors={hasTargetingErrors}
      />

      {userAttributes === undefined && <Loader />}
      {userAttributes !== undefined && (
        <UserAttributesTargeting
          userAttributes={userAttributes}
          onTargetingChange={onTargetingChange}
          targeting={targeting}
          onShowUpsellModal={onUpsell}
          entityType={isOffsite ? 'invitation' : 'survey'}
        />
      )}
      <TrafficCoverage
        widget={isOffsite ? 'invitation' : 'survey'}
        percentage={targetingPercentage}
        onUpsell={onUpsell}
        onPercentageChanged={onTargetingPercentageChange}
      />
      <MultipleWidgetHelp />
    </Stack>
  );
};
